import * as Comlink from 'comlink';
import type {
  EncryptedPhotoPayload,
  EncryptWorkerApi,
  ReencryptedPhotoPayload,
} from '../../workers/encryptWorker.js';

interface PooledWorker {
  worker: Worker;
  api: Comlink.Remote<EncryptWorkerApi>;
  inFlight: number;
}

// Web Worker pool that parallelises crypto-vault encryption off the main thread.
// We pick the least-busy worker per job (cheap N=poolSize lookup) instead of a
// queue, so a slow file doesn't head-of-line block other workers.
export class EncryptWorkerPool {
  private readonly workers: PooledWorker[];

  constructor(poolSize: number) {
    this.workers = Array.from({ length: poolSize }, () => {
      const worker = new Worker(new URL('../../workers/encryptWorker.ts', import.meta.url), {
        type: 'module',
      });
      return {
        worker,
        api: Comlink.wrap<EncryptWorkerApi>(worker),
        inFlight: 0,
      };
    });
  }

  async encrypt(file: File, albumKey: CryptoKey): Promise<EncryptedPhotoPayload> {
    const slot = this.pickLeastBusy();
    slot.inFlight++;
    try {
      return await slot.api.encrypt(file, albumKey);
    } finally {
      slot.inFlight--;
    }
  }

  async reencrypt(
    packed: string,
    encryptedMeta: string,
    oldAlbumKey: CryptoKey,
    newAlbumKey: CryptoKey,
  ): Promise<ReencryptedPhotoPayload> {
    const slot = this.pickLeastBusy();
    slot.inFlight++;
    try {
      return await slot.api.reencrypt(packed, encryptedMeta, oldAlbumKey, newAlbumKey);
    } finally {
      slot.inFlight--;
    }
  }

  terminate(): void {
    for (const slot of this.workers) {
      slot.worker.terminate();
    }
  }

  private pickLeastBusy(): PooledWorker {
    let best = this.workers[0]!;
    for (const slot of this.workers) {
      if (slot.inFlight < best.inFlight) best = slot;
    }
    return best;
  }
}
